import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LAYOUTS, TYPOGRAPHY, RESPONSIVE, SPACING } from "@/design-system";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

interface Breadcrumb {
  label: string;
  href: string;
}

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  className?: string;
  centered?: boolean;
}

/**
 * PageHeader - Standalone header component for public pages
 * 
 * Uses centralized LAYOUTS and TYPOGRAPHY design tokens.
 * For pages using PublicPageLayout, prefer using its built-in header props.
 */
export const PageHeader = ({ title, subtitle, action, breadcrumbs, className = "", centered = false }: PageHeaderProps) => {
  const { lCommon } = useLocalizations();
  // Only plain string headings can name the browser tab; localized elements are
  // left to SEOHead on public pages.
  useDocumentTitle(typeof title === 'string' ? title : undefined);
  
  
  return (
    <div className={cn(LAYOUTS.headerWrapper, className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className={cn(SPACING.sectionHeader)} aria-label={lCommon('aria.breadcrumb', { defaultValue: 'Breadcrumb navigation' })}>
          <ol className={cn(TYPOGRAPHY.cardDescription, RESPONSIVE.flexRowCenterGap2)} itemScope itemType="https://schema.org/BreadcrumbList">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.href} className={cn(RESPONSIVE.flexRowCenterGap2)} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link 
                  to={crumb.href} 
                  className="hover:text-foreground transition-colors"
                  itemProp="item"
                >
                  <span itemProp="name">{crumb.label}</span>
                </Link>
                <meta itemProp="position" content={String(index + 1)} />
                {index < breadcrumbs.length - 1 && (
                  <ChevronRight className="h-4 w-4" />
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      {/* Header Content */}
      <div className={centered ? undefined : LAYOUTS.headerWithAction}>
        <div className={centered ? LAYOUTS.headerContentCentered : LAYOUTS.headerContent}>
          <h1 className={TYPOGRAPHY.pageTitle}>{title}</h1>
          {subtitle && (
            <p className={TYPOGRAPHY.pageDescription}>{subtitle}</p>
          )}
        </div>
        {action && (
          <div className="flex-shrink-0 w-full sm:w-auto">
            {action}
          </div>
        )}
      </div>
    </div>
  );
};
